import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PendingSponsorshipsBadge } from "./PendingSponsorshipsBadge";
import { useSponsorshipStore } from "@/stores/sponsorship/sponsorship-store";

export const PendingSponsorshipsDropdown: React.FC = () => {
  const { pendingAsSub, fetchAll, acceptBundle, declineBundle } = useSponsorshipStore();
  const [processingBundleId, setProcessingBundleId] = useState<string | null>(null);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleAccept = async (bundleId: string) => {
    setProcessingBundleId(bundleId);
    try {
      await acceptBundle(bundleId);
      toast.success("Sponsorship accepted");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to accept invite");
    } finally {
      setProcessingBundleId(null);
    }
  };

  const handleDecline = async (bundleId: string) => {
    setProcessingBundleId(bundleId);
    try {
      await declineBundle(bundleId);
      toast.success("Sponsorship declined");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to decline invite");
    } finally {
      setProcessingBundleId(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Pending sponsorships">
          <PendingSponsorshipsBadge />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {pendingAsSub.length === 0 ? (
          <DropdownMenuItem disabled>No pending sponsorships</DropdownMenuItem>
        ) : null}
        {pendingAsSub.map((bundle) => (
          <DropdownMenuItem key={bundle._id} className="flex flex-col items-stretch gap-2">
            <span className="text-sm">{bundle.invitedEmail}</span>
            <span className="flex gap-2">
              <Button
                size="sm"
                disabled={processingBundleId === bundle._id}
                onClick={() => void handleAccept(bundle._id)}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={processingBundleId === bundle._id}
                onClick={() => void handleDecline(bundle._id)}
              >
                Decline
              </Button>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
